import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { TreePine, ArrowLeft, Share2, Trophy, Flame } from 'lucide-react';

export default function StreakTree() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => { if (user) api.get('/games/state').then(r => setStats(r.data)).catch(() => {}); }, [user]);

  const streak = stats?.streak || 0;
  const xp = stats?.xp || 0;
  const level = stats?.level || 1;
  // Each leaf = 1 day completed, max 30 leaves visible.
  const leaves = Math.min(30, streak);
  const stage = leaves < 5 ? 'sapling' : leaves < 15 ? 'growing' : leaves < 25 ? 'thriving' : 'golden';

  // Build leaves arranged in branches
  const leafPositions = [];
  for (let i = 0; i < 30; i++) {
    const angle = (i / 30) * Math.PI * 2;
    const r = 90 + Math.sin(i * 1.3) * 30 + Math.random() * 8;
    const x = 200 + Math.cos(angle) * r * 0.9;
    const y = 220 - Math.abs(Math.sin(angle) * r) - (i % 3) * 8;
    leafPositions.push({ x, y, filled: i < leaves });
  }

  return (
    <div className="bg-white min-h-screen">
      <div className="kc-container py-6">
        <Link to="/games" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-muted-kc hover:text-gold" data-testid="back-games"><ArrowLeft size={12}/> Games</Link>
        <div className="mt-4 text-center">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold-2)] font-semibold"><TreePine size={12}/> Your Mindset Tree</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl mt-2">Watch yourself grow.</h1>
          <p className="mt-2 text-ink-2 max-w-lg mx-auto">Each day you check in — lesson, game, journal, breath — a gold leaf appears. 30 days makes a fully golden tree.</p>
        </div>

        <div className="mt-6 mx-auto max-w-2xl bg-gradient-to-b from-ink to-black rounded-[var(--kc-radius-md)] p-6 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(400px 200px at 50% 80%, rgba(212,164,55,0.15), transparent 60%)' }}/>
          <svg viewBox="0 0 400 260" className="w-full h-auto" data-testid="tree-svg">
            {/* trunk + branches */}
            <path d="M200 260 L200 180 Q200 150 220 130 Q230 115 215 100 M200 180 Q200 150 180 130 Q170 115 185 100 M200 150 L200 110" stroke="#5b3a1a" strokeWidth="5" fill="none" strokeLinecap="round"/>
            {/* leaves */}
            {leafPositions.map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r={p.filled ? 7 : 4}
                fill={p.filled ? '#D4A437' : 'rgba(255,255,255,0.1)'}
                stroke={p.filled ? '#F0C660' : 'rgba(255,255,255,0.15)'}
                strokeWidth="1">
                {p.filled && <animate attributeName="r" values="7;9;7" dur="3s" repeatCount="indefinite" begin={`${(i % 10) * 0.2}s`}/>}
              </circle>
            ))}
            {/* ground */}
            <line x1="40" y1="260" x2="360" y2="260" stroke="#D4A437" strokeWidth="1" strokeOpacity="0.5"/>
          </svg>
          <div className="text-center text-white">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold">{stage} stage</div>
            <div className="mt-2 grid grid-cols-3 gap-6 max-w-md mx-auto">
              <Stat icon={Flame}   label="Streak" value={`${streak}d`} testid="st-streak"/>
              <Stat icon={TreePine} label="Leaves" value={leaves} testid="st-leaves"/>
              <Stat icon={Trophy}  label="Level"  value={level} testid="st-level"/>
            </div>
            <div className="mt-4 text-xs text-white/60">{xp} XP earned</div>
          </div>
        </div>

        <div className="mt-5 flex justify-center gap-3">
          <Link to="/games" className="h-12 px-6 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] border border-kc-strong hover:bg-surface-2 font-semibold">More games</Link>
          <button onClick={() => { if (navigator.share) navigator.share({title: 'My Mindset Tree', text: `Day ${streak} of training my mind — Kickass Course.`, url: window.location.href}).catch(()=>{}); }} className="h-12 px-6 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold" data-testid="st-share"><Share2 size={14}/> Share</button>
        </div>
        {!user && <div className="mt-4 text-center text-sm text-muted-kc">Log in to start growing your tree.</div>}
      </div>
    </div>
  );
}

const Stat = ({ icon: I, label, value, testid }) => (
  <div><I className="mx-auto text-[color:var(--kc-gold)] mb-1" size={16}/><div className="font-display text-2xl text-[color:var(--kc-gold)] tabular-nums" data-testid={testid}>{value}</div><div className="text-[10px] uppercase tracking-wider text-white/60">{label}</div></div>
);
